import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import api, { API } from "@/lib/api";
import { LOGO, CONTACT } from "@/data/site";
import Room3D from "@/components/studio/Room3D";

const CadShared = ({ data }) => {
  const [cols, setCols] = useState([]);
  const [meta, setMeta] = useState({});
  const [brass, setBrass] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/collections"), api.get("/cad/fixtures")]).then(([c, f]) => { setCols([...(c.data || []), ...(f.data?.panels || [])]); setBrass(f.data?.brassware || []); setMeta(Object.fromEntries((f.data?.items || []).map((x) => [x.id, x]))); }).catch(() => {});
  }, []);
  const s = data.cad_summary || { tiles: [], fixtures: [] };
  return (
    <div className="space-y-6" data-testid="shared-cad">
      <div className="h-[420px] rounded-2xl overflow-hidden border border-rossi-gold/20" data-testid="shared-cad-3d">
        {cols.length > 0 && <Room3D state={data.cad_state} collections={cols} meta={meta} brassware={brass} readOnly onSelect={() => {}} />}
      </div>
      <div className="card-lux p-6 text-sm" data-testid="shared-cad-list">
        <p className="eyebrow mb-3">Materials &amp; fixtures</p>
        {s.tiles.map((t) => <div key={t.id} className="flex justify-between py-2 border-b border-white/5"><span className="text-rossi-cream">{t.name} <span className="text-xs text-rossi-stone">{t.order_m2} m²</span></span><span className="font-mono text-rossi-gold">£{Math.round(t.est_gbp)}</span></div>)}
        {(s.panels || []).map((p) => <div key={p.id} className="flex justify-between py-2 border-b border-white/5"><span className="text-rossi-cream">{p.name} splashback <span className="text-xs text-rossi-stone">× {p.qty}</span></span><span className="font-mono text-rossi-gold">£{Math.round(p.est_gbp)}</span></div>)}
        {s.fixtures.map((f, i) => <div key={i} className="flex justify-between py-2 border-b border-white/5"><span className="text-rossi-cream/90">{f.name}</span><span className="font-mono text-rossi-gold">£{Math.round(f.price_gbp)}</span></div>)}
        <div className="flex justify-between pt-3"><span className="text-rossi-stone">Guide materials estimate</span><span className="font-mono text-rossi-gold text-lg">£{Math.round(s.est_total_gbp || 0)}</span></div>
      </div>
    </div>
  );
};

const imgUrl = (id) => `${API}/studio/image/${id}`;

export default function SharedDesign() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/designs/${id}/share`).then((r) => setData(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-rossi-gold" /></div>;
  if (err || !data) return <div className="container-rossi py-24 text-center"><p className="text-rossi-cream/70">This design link is no longer available.</p><Link to="/studio" className="btn-gold mt-6 inline-flex">Design your own</Link></div>;

  const images = data.final_image_id ? [data.final_image_id] : (data.renders || []);

  return (
    <div className="min-h-screen" data-testid="shared-design-page">
      <Seo title="A Rossi Ceramics design concept" description="A bathroom design concept created in The Studio by Rossi Ceramics." />
      <section className="container-rossi py-16 max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="Rossi Ceramics" className="h-10 w-10 object-contain rounded-lg bg-white/90 p-1" />
          <div>
            <p className="eyebrow">The Studio · Shared design</p>
            <h1 className="font-serif text-3xl text-rossi-cream">{data.kind === "cad" ? "A 3D Studio design" : `${data.style} ${data.room_type}`}</h1>
          </div>
        </div>
        {data.kind === "cad" && <CadShared data={data} />}
        <div className="grid gap-5 mt-6">
          {images.map((imgId, i) => (
            <img key={imgId} src={imgUrl(imgId)} alt={`Design ${i + 1}`} className="w-full rounded-2xl border border-rossi-gold/20" data-testid={`shared-image-${i}`} />
          ))}
        </div>
        <div className="card-lux p-7 mt-8 text-center">
          <Sparkles size={26} className="text-rossi-gold mx-auto" />
          <h2 className="font-serif text-2xl text-rossi-cream mt-3">Love this look?</h2>
          <p className="text-sm text-rossi-cream/70 mt-2">Create your own in The Studio, or book a free consultation with Rossi Ceramics.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Link to="/studio" className="btn-gold" data-testid="shared-cta-studio"><Sparkles size={16} /> Design your space</Link>
            <Link to="/booking" className="btn-ghost-gold">Book a consultation</Link>
            <a href={CONTACT.phoneHref} className="btn-ghost-gold">Call {CONTACT.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
